import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withFormik } from 'formik';
import moment from 'moment';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import qs from 'qs';

import { Alert } from '@lightspeed/flame/Alert';
import Layout, { LayoutContent } from 'cirrus/Layout';
import PageProgress from 'pages/components/PageProgress';
import Text from '@lightspeed/cirrus-text';

import { actionTypes } from 'pages/store/actions/defaults';
import { getData, putData, postData, clearData } from 'pages/store/actions/data';
import { getValue, getMeta, getLinks, getProcessing, getErrors } from 'pages/store/selectors';
import { entities, endpoints } from 'pages/store/schema';
import { DB_DATE_FORMAT } from 'pages/utils/time';
import LocalizeContext from 'pages/context/LocalizeContext';

import VendorReturnActionBar from './VendorReturnActionBar';
import VendorReturnAddItem from './VendorReturnAddItem';
import VendorReturnDetails from './VendorReturnDetails';
import VendorReturnEdit from './VendorReturnEdit';
import VendorReturnForm from './VendorReturnForm';
import VendorReturnItemListing from './VendorReturnItemListing';
import VendorReturnVendorDetails from './VendorReturnVendorDetails';
import { setBreadcrumbs, formatMoney, guiDefsAddItem, keyExists } from './utils';

import { layoutCls } from './VendorReturn.style';

const hasEditVendorRight = () => window.merchantos.session.hasRight('vendors');
const dateFormat = DB_DATE_FORMAT;

export class VendorReturn extends Component {
  constructor(props) {
    super(props);

    this.state = {
      vendorReturnID: get(props, 'match.params.id'),
      isModalOpen: false,
      isHiding: false,
      isAddingItem: false,
      isArchiving: false,
      isRestoring: false,
      isSending: false,
      isReopening: false,
      isClosing: false,
    };

    this.getVendorReturnData = this.getVendorReturnData.bind(this);
    this.handleVendorDetailsClick = this.handleVendorDetailsClick.bind(this);
    this.handleEditVendorClick = this.handleEditVendorClick.bind(this);
    this.handleArchiveClick = this.handleArchiveClick.bind(this);
    this.handleAddItem = this.handleAddItem.bind(this);
    this.handleRestoreClick = this.handleRestoreClick.bind(this);
    this.handleSendClick = this.handleSendClick.bind(this);
    this.handleReopenClick = this.handleReopenClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleSentDateChange = this.handleSentDateChange.bind(this);
    this.handleClosedDateChange = this.handleClosedDateChange.bind(this);
    this.handleOpenModal = this.handleOpenModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
  }

  componentDidMount() {
    const { vendorReturn } = this.props;

    this.updateBreadcrumbs();

    if (!vendorReturn) {
      this.getVendorReturnData();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      location = {},
      loadingOrSavingErrors,
      vendorReturn,
      isSaving,
      /* formik */
      resetForm,
      setErrors,
      setSubmitting,
    } = this.props;

    if (prevProps.isSaving && !isSaving) {
      resetForm();
      if (loadingOrSavingErrors) {
        setErrors(this.props.errors);
      } else {
        window.flashNotifier('success', 'Vendor return updated', 3);
      }
      setSubmitting(false);
    }

    if (
      (!prevProps.vendorReturn && vendorReturn) ||
      get(prevProps.vendorReturn, 'total.value') !== get(vendorReturn, 'total.value') ||
      location.search !== prevProps.location.search
    ) {
      this.updateBreadcrumbs();
    }
  }

  componentWillUnmount() {
    document.getElementById('react-page').classList.remove('cr-bg-snow');
    this.props.clearData();
  }

  getVendorReturnData() {
    const { vendorReturnID } = this.state;
    this.props.getData(vendorReturnID);
  }

  updateBreadcrumbs() {
    const { vendorReturn = {} } = this.props;
    const hasVendorReturnKey = keyExists(vendorReturn);
    const currencySymbol = get(window, 'merchantos.locale.currency_symbol', '');
    const vendorReturnID = get(this.props, 'vendorReturn.vendorReturnID', '');
    const vendorName = get(this.props, 'vendorReturn.Vendor.data.name', '');
    const totalValue =
      currencySymbol + formatMoney(get(this.props, 'vendorReturn.total.value', '0'));

    let varText = `#${vendorReturnID}${vendorName ? `, ${vendorName}` : ''}`;
    if (hasVendorReturnKey('total')) {
      varText += `, ${totalValue}`;
    }

    setBreadcrumbs({
      text: 'Vendor return',
      record_id: vendorReturnID,
      varText,
    });
  }

  handleVendorDetailsClick() {
    const {
      vendorReturn: { vendorReturnID: id, hideVendorDetails: isVendorDetailsHidden } = {},
    } = this.props;

    this.setState({ isHiding: true });
    this.props[isVendorDetailsHidden ? 'showVendorReturns' : 'hideVendorReturns']({ id }).then(
      () => {
        this.setState({ isHiding: false });
      }
    );
  }

  handleEditVendorClick() {
    const { vendorReturn: { Vendor: { data: vendorData = {} } = {} } = {} } = this.props;
    const { vendorID } = vendorData || {};
    window.navigateToContent('view', 'vendor.views.vendor', vendorID);
  }

  handleArchiveClick({ onDone = () => {} }) {
    const { vendorReturn: { vendorReturnID: id } = {}, archiveVendorReturns } = this.props;

    this.setState({ isArchiving: true });
    archiveVendorReturns({
      id,
    }).then(() => {
      // On Stencil the menu will be removed after archiving so there's no reason to close it.
      if (!window.isGriffin()) {
        onDone();
      }
      this.setState({ isArchiving: false });
    });
  }

  handleRestoreClick() {
    const { vendorReturn: { vendorReturnID: id } = {}, restoreVendorReturns } = this.props;

    this.setState({ isRestoring: true });
    restoreVendorReturns({
      id,
    }).then(() => {
      this.setState({ isRestoring: false });
    });
  }

  handleSendClick() {
    const {
      vendorReturn: { vendorReturnID: id } = {},
      sendVendorReturns,
      getListingData,
    } = this.props;

    this.setState({ isSending: true });
    sendVendorReturns({
      id,
      onSuccess: () => {
        getListingData({
          vendorReturnID: id,
          ...qs.parse(window.location.search, { ignoreQueryPrefix: true }),
        });
        window.flashNotifier('success', 'Vendor return sent', 3);
      },
    }).then(() => {
      this.setState({ isSending: false });
    });
  }

  handleReopenClick({ onDone = () => {} }) {
    const {
      vendorReturn: { vendorReturnID: id } = {},
      reopenVendorReturns,
      getListingData,
    } = this.props;

    this.setState({ isReopening: true });
    reopenVendorReturns({
      id,
    }).then(() => {
      onDone();
      getListingData({
        vendorReturnID: id,
        ...qs.parse(window.location.search, { ignoreQueryPrefix: true }),
      });
      this.setState({ isReopening: false });
    });
  }

  handleCloseClick() {
    const { vendorReturn: { vendorReturnID: id } = {}, closeVendorReturns } = this.props;

    this.setState({ isClosing: true });
    closeVendorReturns({
      id,
      onSuccess: () => window.flashNotifier('success', 'Vendor return closed', 3),
    }).then(() => {
      this.setState({ isClosing: false });
    });
  }

  handleSentDateChange({ date }) {
    const { setFieldValue } = this.props;
    setFieldValue('sentDate', moment(date).format(DB_DATE_FORMAT));
  }

  handleClosedDateChange({ date }) {
    const { setFieldValue } = this.props;
    setFieldValue('closedDate', moment(date).format(DB_DATE_FORMAT));
  }

  handleOpenModal(event) {
    event.preventDefault();

    this.setState({ isModalOpen: true });
  }

  handleCloseModal() {
    this.setState({ isModalOpen: false });
  }

  handleAddItem({ value, onDone }) {
    let {
      vendorReturn: { Shop: { data: shop } = {}, Vendor: { data: vendor } = {} } = {},
    } = this.props;
    const { vendorReturn: { vendorReturnID } = {} } = this.props;
    const { isAddingItem } = this.state;
    shop = shop || {};
    vendor = vendor || {};

    /**
     * On Stencil if we scanned without focusing on the input we should blurred after the keys are being filled in.
     * The input will be focused back if needed after the item is added.
     */
    if (window.isGriffin()) {
      document.activeElement.blur();
    }

    /**
     * To prevent the event being triggered twice.
     */
    if (!isAddingItem) {
      this.setState(
        {
          isAddingItem: true,
        },
        () =>
          guiDefsAddItem({
            value,
            vendorReturnID,
            vendorID: vendor.vendorID,
            shopID: shop.shopID,
            onDone: () => {
              this.setState(
                {
                  isAddingItem: false,
                },
                () => {
                  onDone();
                  this.props.getData(vendorReturnID);
                  this.props.getListingData({
                    vendorReturnID,
                    ...qs.parse(window.location.search, { ignoreQueryPrefix: true }),
                  });
                  window.flashNotifier('success', 'Item Added.', 3);
                }
              );
            },
          })
      );
    }
  }

  render() {
    let {
      isInitializing,
      vendorReturn: {
        Employee: { data: employee } = {},
        Shop: { data: shop } = {},
        Vendor: { data: vendor } = {},
        Items: { data: items } = {},
      } = {},
    } = this.props;
    const {
      isLoading,
      isItemsSaving,
      isSaving,
      meta: { fields: { update: enabledFields = [] } = {} } = {},
      links: {
        addItems: addItemsLink,
        archive: archiveLink,
        restore: restoreLink,
        send: sendLink,
        close: closeLink,
        reopen: reopenLink,
        updateItem: updateItemLink,
      } = {},
      vendorReturn = {},
      loadingOrSavingErrors,
      /* formik */
      values,
      errors,
      dirty,
      isSubmitting,
      setFieldError,
      setFieldValue,
      submitForm,
      handleSubmit,
    } = this.props;
    const { status, archived: isArchived, hideVendorDetails: isVendorDetailsHidden, vendorID } =
      vendorReturn || {};
    /* data null fix  */
    employee = employee || {};
    shop = shop || {};
    vendor = vendor || {};
    items = items || [];

    const {
      vendorReturnID,
      isModalOpen,
      isHiding,
      isAddingItem,
      isArchiving,
      isRestoring,
      isSending,
      isReopening,
      isClosing,
    } = this.state;

    isInitializing = isEmpty(vendorReturn) && isLoading;

    return (
      <LocalizeContext.Provider>
        <Layout isLoading={isSaving || isAddingItem}>
          <PageProgress
            hidden={!isSaving && !isAddingItem}
            progress={isSaving || isAddingItem ? 'start' : 'complete'}
          />
          <VendorReturnActionBar
            vendorReturnID={vendorReturnID}
            /* state */
            isArchiving={isArchiving}
            isClosing={isClosing}
            isItemsSaving={isItemsSaving}
            isVendorReturnLoading={isLoading}
            isLoading={isInitializing}
            isSaving={isSaving}
            isSending={isSending}
            isReopening={isReopening}
            isRestoring={isRestoring}
            /* callbacks */
            onArchive={archiveLink ? this.handleArchiveClick : null}
            onClose={closeLink ? this.handleCloseClick : null}
            onReopen={reopenLink ? this.handleReopenClick : null}
            onRestore={restoreLink ? this.handleRestoreClick : null}
            onSend={sendLink ? this.handleSendClick : null}
            /* formik */
            dirty={dirty}
            submitForm={submitForm}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
          <LayoutContent css={layoutCls}>
            {loadingOrSavingErrors && !isModalOpen ? (
              <Alert type="danger" title="Error" className="cr-width-full cr-mb-4">
                {get(
                  loadingOrSavingErrors,
                  'response.data.errors[0].detail',
                  'An error has occurred. Please reload the page and try again'
                )}
              </Alert>
            ) : null}
            <div className="cr-flex cr-mb-3">
              <Text heading2 data-test="vendor-return-title">
                Vendor return <var>#{vendorReturnID}</var>
              </Text>
              <VendorReturnEdit
                shopID={shop.shopID}
                vendorID={vendor.vendorID}
                vendorReturnID={vendorReturnID}
                enabledFields={enabledFields}
                /* state */
                isOpen={isModalOpen}
                isArchived={isArchived}
                /* callbacks */
                onOpenModal={this.handleOpenModal}
                onCloseModal={this.handleCloseModal}
              />
            </div>
            <VendorReturnDetails
              dateFormat={dateFormat}
              employee={employee}
              enabledFields={enabledFields}
              shop={shop}
              vendor={vendor}
              vendorReturn={vendorReturn}
              /* state */
              isArchived={isArchived}
              isLoading={isInitializing}
              isSubmitting={isSubmitting}
              /* callbacks */
              onSentDateChange={this.handleSentDateChange}
              onClosedDateChange={this.handleClosedDateChange}
              /* formik */
              values={values}
            />
            <VendorReturnVendorDetails
              vendor={vendor}
              /* state */
              isHiding={isHiding}
              isLoading={isInitializing}
              isVendorDetailsHidden={isVendorDetailsHidden}
              isVendorEditEnabled={hasEditVendorRight()}
              /* callbacks */
              onVendorDetailsChange={this.handleVendorDetailsClick}
              onEditVendorClick={this.handleEditVendorClick}
            />
            <VendorReturnForm
              enabledFields={enabledFields}
              vendorReturn={vendorReturn}
              /* state */
              isClosed={vendorReturn.status === 'done'}
              isLoading={isInitializing}
              /* formik */
              errors={errors}
              values={values}
              setFieldError={setFieldError}
              setFieldValue={setFieldValue}
            />
            {status === 'open' && !isArchived && (
              <VendorReturnAddItem
                isAddingItem={isAddingItem}
                isDisabled={!addItemsLink}
                onAddItem={this.handleAddItem}
              />
            )}
            <VendorReturnItemListing
              vendorID={vendorID}
              vendorReturnID={vendorReturnID}
              itemsExist={!!items.length}
              /* state */
              isArchived={isArchived}
              updateItem={!!updateItemLink}
            />
          </LayoutContent>
        </Layout>
      </LocalizeContext.Provider>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  return {
    vendorReturn: getValue(entities.vendorReturns, id)(state),
    meta: getMeta({ entity: entities.vendorReturns, list: false })(state),
    links: getLinks({ entity: entities.vendorReturns, list: false })(state),
    isLoading: getProcessing({
      entity: entities.vendorReturns,
      list: false,
      actionType: actionTypes.LOADING,
    })(state),
    isSaving: getProcessing({
      entity: entities.vendorReturns,
      list: false,
      actionType: actionTypes.SAVING,
    })(state),
    isItemsSaving: getProcessing({
      entity: entities.vendorReturnItem,
      list: false,
      actionType: actionTypes.SAVING,
    })(state),
    loadingOrSavingErrors:
      getErrors({ entity: entities.vendorReturns, list: false, actionType: actionTypes.LOADING })(
        state
      ) ||
      getErrors({ entity: entities.vendorReturns, list: false, actionType: actionTypes.SAVING })(
        state
      ),
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  getData: id => dispatch(getData(endpoints.vendorReturns.view({ id }))),
  getListingData: ({ vendorReturnID, page, sort, filter }) =>
    dispatch(getData(endpoints.vendorReturnItem.list({ vendorReturnID, page, sort, filter }))),
  putDataVendorReturns: ({ id, payload, onSuccess }) =>
    dispatch(putData(endpoints.vendorReturns.edit({ id, payload, onSuccess }))),
  archiveVendorReturns: ({ id, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.archive({ id, onSuccess }))),
  restoreVendorReturns: ({ id, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.restore({ id, onSuccess }))),
  sendVendorReturns: ({ id, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.send({ id, onSuccess }))),
  reopenVendorReturns: ({ id, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.reopen({ id, onSuccess }))),
  closeVendorReturns: ({ id, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.close({ id, onSuccess }))),
  hideVendorReturns: ({ id }) => dispatch(postData(endpoints.vendorReturns.hide({ id }))),
  showVendorReturns: ({ id }) => dispatch(postData(endpoints.vendorReturns.show({ id }))),
  clearData: () => dispatch(clearData([entities.vendorReturns])),
});

const formSettings = {
  enableReinitialize: true,
  mapPropsToValues: ({ vendorReturn = {} }) => {
    const {
      refNum = '',
      Note: { data: noteData } = {},
      shipCost: { value: shipCost = 0 } = {},
      otherCost: { value: otherCost = 0 } = {},
      sentDate,
      closedDate,
    } = vendorReturn;
    return {
      refNum,
      sentDate,
      closedDate,
      Note: { note: noteData && noteData.note },
      shipCost: formatMoney(shipCost),
      otherCost: formatMoney(otherCost),
    };
  },
  handleSubmit: (
    values,
    {
      props: {
        putDataVendorReturns,
        vendorReturn,
        meta: { fields: { update: enabledFields = [] } = {} } = {},
      },
      setSubmitting,
    }
  ) => {
    setSubmitting(true);
    putDataVendorReturns({
      id: vendorReturn.vendorReturnID,
      payload: {
        vendorReturn: pick(
          {
            sentDate: values.sentDate,
            closedDate: values.closedDate,
            refNum: values.refNum,
            Note: { note: values.Note.note },
            shipCost: { value: values.shipCost },
            otherCost: { value: values.otherCost },
          },
          enabledFields
        ),
      },
    })
      .then(() => {
        window.flashNotifier('success', 'Saved Changes', 3);
      })
      .catch(() => {
        // silence error from here, handling is done inside componentDidUpdate
      });
  },
};

const ConnectedVendorReturn = connect(
  mapStateToProps,
  mapDispatchToProps
)(withFormik(formSettings)(VendorReturn));

export default ConnectedVendorReturn;
